import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as ClientApi from '~/lib/ClientApi';
import {
    RequestError,
    type CustomQuestionConfirmation,
} from './CustomQuestionApi';
import { sampleConfirmation, withSampleEventCanceled } from './sampleData';

/**
 * What breely.confirmation is and how breely.cancelEvent is carried out, for
 *  one custom question.
 *
 * On the confirmation page the cancel goes through the same public route the
 *  page's own Cancel button uses, authorized by the submission's token - which
 *  stays out here and never reaches the frame. That route holds the business's
 *  scheduling limits; the checks below just answer sooner, and with a reason
 *  the question can act on.
 *
 * In preview there is nothing real to cancel, so the question gets the sample
 *  confirmation and cancelling changes only that.
 */
export default function useConfirmationActions({
    isConfirmationPage,
    isPreview,
    confirmation,
    token,
}: {
    isConfirmationPage: boolean;
    isPreview: boolean;
    confirmation?: CustomQuestionConfirmation | null;
    token?: string | null;
}) {
    const queryClient = useQueryClient();

    // Made when it is first needed rather than on mount, since the definition
    //  that decides it can arrive after the question does
    const baseSample = useMemo(
        () => (isPreview ? sampleConfirmation() : null),
        [isPreview],
    );
    const [canceledSample, setSample] =
        useState<CustomQuestionConfirmation | null>(null);

    const current = isConfirmationPage
        ? (confirmation ?? null)
        : isPreview
          ? (canceledSample ?? baseSample)
          : null;

    // The handler outlives renders, so it reads the confirmation from here
    const currentRef = useRef(current);
    currentRef.current = current;

    // A question that calls cancelEvent twice for the same event, ex. from a
    //  double click, gets one request
    const inFlight = useRef<
        Map<string, Promise<CustomQuestionConfirmation | null>>
    >(new Map());

    const cancelEvent = useCallback(
        (eventId: unknown): Promise<CustomQuestionConfirmation | null> => {
            const event = currentRef.current?.events?.find(
                e => `${e.id}` === `${eventId}`,
            );

            if (!event) {
                return Promise.reject(new Error(RequestError.NOT_FOUND));
            }

            if (!event.canCancel) {
                return Promise.reject(new Error(RequestError.CANNOT_CANCEL));
            }

            if (isPreview) {
                const next = withSampleEventCanceled(
                    currentRef.current!,
                    event.id,
                );
                setSample(next);

                return Promise.resolve(next);
            }

            const key = `${event.id}`;
            const existing = inFlight.current.get(key);

            if (existing) {
                return existing;
            }

            const request = ClientApi.post(
                `/event/${token}/${event.id}/cancel`,
                {},
            )
                .then(() => ClientApi.get(`/submission/${token}`))
                .then(fresh => {
                    // The rest of the page reads the same query, so the
                    //  built-in event details catch up too
                    queryClient.setQueryData(['submission', token], fresh);

                    return fresh?.customQuestionConfirmation ?? null;
                })
                .catch(err => {
                    throw new Error(
                        err?.response?.data?.message ||
                            err?.message ||
                            'The event could not be canceled',
                    );
                })
                .finally(() => inFlight.current.delete(key));

            inFlight.current.set(key, request);

            return request;
        },
        [isPreview, token, queryClient],
    );

    const canAct = isPreview || (isConfirmationPage && !!token);

    return {
        confirmation: current,
        onCancelEvent: canAct ? cancelEvent : undefined,
    };
}
